import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/shared/StatusPill";
import { AIBadge } from "@/components/shared/AICard";
import { api } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Download, Check, AlertTriangle, Loader2, ExternalLink, Send } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useMe } from "@/api/hooks";

export function DocumentPanel({ doc, open, onOpenChange }) {
  const qc = useQueryClient();
  const { openComposer } = useApp();
  const { data: me } = useMe();
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const hasOriginal = Boolean(doc?.storagePath);
  // Item 5 — Finance must never be able to approve/confirm/accept a
  // Quotation-category document from here. The generic "Approve document"
  // action below is a document-QA check (not fraudulent/mismatched — the
  // same button legitimately used for invoices, PODs, etc.) and Finance's
  // approve_documents capability is real and still needed for those. The
  // one narrow carve-out: when the document being viewed IS a quotation
  // (doc.type === "Quotation", the value AutoFileDialog/Workspace.jsx's
  // final-quotation upload and the generated quotation PDF both use),
  // Finance loses the button — that authority belongs only to Sales'
  // existing accepted-quotation confirmation workflow. Other roles
  // (Operations/Management) that hold approve_documents are unaffected;
  // only Finance is blocked, and only for Quotation-type documents.
  const isQuotationDoc = String(doc?.type || "").trim().toLowerCase() === "quotation";
  const financeBlockedFromQuotation = me?.role === "finance" && isQuotationDoc;
  const canApprove = Boolean(me?.caps?.approve_documents) && !financeBlockedFromQuotation;

  // Fetch the original uploaded file (authenticated) and build a local blob URL.
  useEffect(() => {
    if (!open || !doc || !hasOriginal) return;
    let revoked = null;
    setLoading(true); setError(false); setFileUrl(null);
    api.get(`/documents/${doc.id}/file`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        revoked = url;
        setFileUrl(url);
        setFileType(res.data.type || "");
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id, hasOriginal]);

  if (!doc) return null;
  const hasExtract = doc.extracted && Object.keys(doc.extracted).length > 0;
  const isPdf = (fileType || "").includes("pdf") || (doc.name || "").toLowerCase().endsWith(".pdf");
  const isImage = (fileType || "").startsWith("image") || /\.(png|jpe?g|webp|heic|heif|gif)$/i.test(doc.name || "");

  const approve = async () => {
    try {
      await api.post(`/documents/${doc.id}/approve`);
      qc.invalidateQueries({ queryKey: ["docs"] });
      toast.success("Document approved & logged to audit trail");
      onOpenChange(false);
    } catch (e) { toast.error("Could not approve"); }
  };

  const download = () => {
    if (!fileUrl) return;
    const a = document.createElement("a");
    a.href = fileUrl; a.download = doc.name || "document";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="document-panel">
        <SheetHeader className="px-5 py-3.5 border-b border-slate-200">
          <SheetTitle className="flex items-center gap-2 font-display">
            <FileText className="h-4 w-4 text-indigo-600" />
            {doc.name}
          </SheetTitle>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono-num">{doc.id}</span>
            <span>·</span>
            <span>{doc.type}</span>
            <span>·</span>
            <StatusPill status={doc.review} />
          </div>
        </SheetHeader>

        <div className="flex items-center gap-1.5 px-5 py-2 border-b border-slate-200 bg-slate-50">
          {hasOriginal && (
            <Button variant="outline" size="sm" className="h-8" data-testid="doc-download" onClick={download} disabled={!fileUrl}>
              <Download className="h-3.5 w-3.5 mr-1" />Download original
            </Button>
          )}
          {fileUrl && (
            <a href={fileUrl} target="_blank" rel="noreferrer" data-testid="doc-open-tab">
              <Button variant="outline" size="sm" className="h-8"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open in new tab</Button>
            </a>
          )}
          {doc.customerId && (
            <Button variant="outline" size="sm" className="h-8" data-testid="doc-send"
              onClick={() => { onOpenChange(false); openComposer({ channel: "email", customerId: doc.customerId, parties: ["Customer"], defaultParty: "Customer", subjectHint: `${doc.inquiryId ? `[${doc.inquiryId}] ` : ""}${doc.name}`, intent: `send ${doc.name} to the customer`, attachments: [{ id: doc.id, name: doc.name }] }); }}>
              <Send className="h-3.5 w-3.5 mr-1" />Send document
            </Button>
          )}
          <div className="ml-auto text-xs text-slate-500">
            {hasOriginal ? "Original uploaded file" : "No original on file"}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {doc.decision && doc.aiRead && (
            <div data-testid="doc-decision" className={`rounded-xl border p-3.5 ${doc.escalated ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center gap-2 mb-1">
                <AIBadge>Assisted intake</AIBadge>
                <span className={`text-xs font-semibold ${doc.escalated ? "text-amber-700" : "text-slate-600"}`}>
                  {doc.escalated ? "Escalated — potential mismatch" : "Routed — awaiting human review"}
                </span>
              </div>
              <div className="text-[13px] text-slate-600">
                Filed to <span className="font-mono-num font-semibold text-slate-800">{doc.routedJobId || "—"}</span>
                {doc.confidence && <span className="ml-1 text-[11px] uppercase tracking-wider text-slate-400">· {doc.confidence} confidence</span>}
              </div>
              {doc.routingReason && <div className="text-[12px] text-slate-500 mt-0.5">{doc.routingReason}</div>}
            </div>
          )}

          {/* Original document preview */}
          <div data-testid="doc-original-preview" className="w-full rounded-lg border border-slate-300 bg-slate-50 overflow-hidden">
            {!hasOriginal ? (
              <div className="min-h-64 bg-white p-8 flex items-center justify-center text-center">
                <div>
                  <FileText className="mx-auto h-9 w-9 text-slate-300" />
                  <div className="mt-3 font-display font-semibold text-slate-800">{doc.type}</div>
                  <div className="mt-1 max-w-xs text-xs text-slate-500">The original file is not available for this imported record. Upload a verified source document from the Job workspace.</div>
                </div>
              </div>
            ) : loading ? (
              <div className="h-96 flex items-center justify-center text-slate-500 text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading original…
              </div>
            ) : error ? (
              <div className="h-40 flex items-center justify-center text-amber-700 text-sm gap-2">
                <AlertTriangle className="h-4 w-4" /> Could not load the original document
              </div>
            ) : isImage ? (
              <img src={fileUrl} alt={doc.name} className="w-full h-auto max-h-[70vh] object-contain bg-white" data-testid="doc-image" />
            ) : isPdf ? (
              <iframe src={fileUrl} title={doc.name} className="w-full h-[70vh] bg-white" data-testid="doc-pdf" />
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-slate-600 text-sm gap-2">
                <FileText className="h-6 w-6 text-slate-400" />
                Preview not supported — use Download to view
              </div>
            )}
          </div>

          {hasExtract && (
            <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-4">
              <div className="flex items-center gap-2 mb-3">
                <AIBadge>Extracted data</AIBadge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(doc.extracted).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold">{k}</div>
                    <div className="text-sm font-medium text-violet-950 font-mono-num">{String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {doc.mismatch && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div><span className="font-semibold">Potential mismatch: </span>{doc.mismatch}</div>
            </div>
          )}

          <div className="text-xs text-slate-500 grid grid-cols-2 gap-2 pt-2">
            <div><span className="text-slate-400">Source:</span> {doc.source}</div>
            <div><span className="text-slate-400">Uploaded by:</span> {doc.uploadedBy}</div>
            <div><span className="text-slate-400">Date:</span> {doc.date}</div>
            <div><span className="text-slate-400">Linked job:</span> {doc.jobId}</div>
          </div>
        </div>

        <div className="border-t border-slate-200 p-4 flex items-center gap-2">
          {String(doc.review || "").toLowerCase() === "approved" ? (
            <div className="flex-1 flex items-center gap-2 text-sm text-emerald-700 font-medium"><Check className="h-4 w-4" />Document approved · no action required</div>
          ) : canApprove ? (
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={approve} data-testid="doc-approve">
              <Check className="h-4 w-4 mr-1.5" /> {doc.escalated ? "Override & approve" : "Approve document"}
            </Button>
          ) : financeBlockedFromQuotation ? (
            <div className="flex-1 text-sm text-slate-500" data-testid="doc-approve-blocked-finance-quotation">Finance can view this quotation as a read-only commercial reference. Only Sales can confirm which quotation the customer accepted.</div>
          ) : (
            <div className="flex-1 text-sm text-slate-500">Awaiting review by an authorised Operations or Finance user.</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
